import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Logo, Brand, BrandText } from "@/components/Logo";
import {
  ArrowRight, Sparkles, MessagesSquare, Newspaper, CalendarCheck, Globe, Crown, Check, Quote,
} from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1636144896336-b056be4a8dfe?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const STORY = "https://images.pexels.com/photos/5879670/pexels-photo-5879670.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"; // eslint-disable-line no-unused-vars

const FEATURES = [
  { icon: MessagesSquare, title: "AI from the culture", body: "Ask anything and get answers grounded in Black history, context, and lived experience — not generic AI." },
  { icon: Newspaper, title: "Daily briefing", body: "Real, fresh headlines across culture, health, finance, business & more — with an in-app aiBLACK take on each story." },  { icon: Globe, title: "Live web search", body: "Pro answers pull live from the web when a question needs current info, with sources you can trust." },
  { icon: CalendarCheck, title: "Your daily assistant", body: "Calendar, to-dos, and saved conversations — the tools that keep your day, and your goals, on track." },
];

export default function Landing() {
  const navigate = useNavigate();
  const go = () => navigate("/auth");

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white overflow-x-hidden">
      {/* Nav */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#0a0a0f]/70 border-b border-[#2a3040]"
        style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <Logo size="text-2xl" />
          <div className="flex items-center gap-3">
            <button data-testid="landing-articles" onClick={() => navigate("/articles")}
              className="hidden sm:block text-sm text-[#9ca3af] hover:text-white transition-colors px-3 py-2">Articles</button>
            <button data-testid="landing-signin" onClick={go}
              className="text-sm text-[#9ca3af] hover:text-white transition-colors px-3 py-2">Sign in</button>
            <button data-testid="landing-get-started" onClick={go}
              className="flex items-center gap-1.5 bg-[#FF5A00] text-[#0a0a0f] font-bold rounded-full px-5 py-2.5 text-sm hover:bg-[#FF7A33] transition-colors">
              Get Started <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative max-w-6xl mx-auto px-5 pt-16 pb-20 grid lg:grid-cols-2 gap-10 items-center">
        <div className="absolute -top-10 -left-20 w-96 h-96 rounded-full bg-[#FF5A00]/15 blur-[120px] pointer-events-none" />
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#FF5A00] font-bold mb-5">
            <Sparkles size={14} /> The Unfair Advantage
          </div>
          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight mb-6">
            AI built for<br />the <span className="text-[#FF5A00]">culture.</span>
          </h1>
          <p className="text-[#c7ccd6] text-lg leading-relaxed max-w-lg mb-8">
            <Brand /> is your daily AI assistant and information platform — answering with the depth, context, and
            voice of the Black community front and center. Knowledge, news, and tools that move you forward.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <button data-testid="hero-get-started" onClick={go}
              className="flex items-center gap-2 bg-[#FF5A00] text-[#0a0a0f] font-bold rounded-full px-7 py-3.5 hover:bg-[#FF7A33] transition-colors">
              Start free <ArrowRight size={18} />
            </button>
            <button onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })}
              className="rounded-full px-7 py-3.5 font-semibold border border-[#2a3040] text-white hover:border-[#FF5A00] transition-colors">
              See Pro
            </button>
          </div>
          <p className="text-xs text-[#6b7280] mt-4">Free to start · 10 chats a day · No card required</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}
          className="relative">
          <div className="relative rounded-3xl overflow-hidden border border-[#2a3040] shadow-2xl">
            <img src={HERO} alt="aiBLACK" className="w-full h-[280px] sm:h-[360px] object-cover object-top" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f]/60 to-transparent" />
          </div>
          <div className="mt-5 bg-[#15151d] border border-[#2a3040] rounded-2xl p-4 max-w-[260px] shadow-xl">
            <div className="text-[10px] uppercase tracking-widest font-bold mb-1"><Brand /></div>
            <p className="text-sm text-[#d1d5db] leading-snug">"Answers with your history, your context, your voice."</p>
          </div>
        </motion.div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-5 py-16">
        <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight mb-3">Everything, in one place.</h2>
        <p className="text-[#9ca3af] mb-10 max-w-xl">Not just a chatbot — a daily platform for knowledge, news, and getting things done.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURES.map((f, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.07 }}
              className="bg-[#15151d] border border-[#2a3040] rounded-2xl p-6 hover:border-[#FF5A00] transition-colors">
              <div className="w-11 h-11 rounded-xl bg-[#FF5A00]/15 flex items-center justify-center mb-4">
                <f.icon size={20} className="text-[#FF5A00]" />
              </div>
              <h3 className="font-display font-bold text-lg mb-2">{f.title}</h3>
              <p className="text-sm text-[#9ca3af] leading-relaxed"><BrandText>{f.body}</BrandText></p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Story */}
      <section className="max-w-6xl mx-auto px-5 py-16 grid lg:grid-cols-2 gap-10 items-center">
        <div className="rounded-3xl overflow-hidden border border-[#2a3040] order-2 lg:order-1 max-w-sm">
          <img src="/reggie.jpg" alt="Reggie and Charen Smith, founders of aiBLACK" className="w-full h-[240px] object-cover object-top" />
        </div>
        <div className="order-1 lg:order-2">
          <Quote size={28} className="text-[#FF5A00] mb-4" />
          <p className="font-display font-bold text-2xl sm:text-3xl leading-snug mb-6">
            "I envision <Brand /> as a living, breathing extension of The Black Experience in cyberspace — a database of
            Black knowledge, Black information, and Black empowerment."
          </p>
          <p className="text-white font-bold">Reggie and Charen Smith</p>
          <p className="text-xs uppercase tracking-[0.2em] text-[#FF5A00] font-bold">Founder & Creator</p>
          <button onClick={() => navigate("/about")} className="mt-5 text-sm text-[#9ca3af] hover:text-[#FF5A00] inline-flex items-center gap-1.5 transition-colors">
            Read our story <ArrowRight size={15} />
          </button>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-5xl mx-auto px-5 py-16">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#FF5A00] font-bold mb-3">
            <Crown size={14} /> Pricing
          </div>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight">Start free. Go Pro when ready.</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-5 max-w-3xl mx-auto">
          <div className="bg-[#15151d] border border-[#2a3040] rounded-2xl p-7">
            <h3 className="font-display font-bold text-xl mb-1">Free</h3>
            <div className="font-display font-black text-4xl mb-5">$0</div>
            {["10 AI chats per day", "Daily news briefing", "Calendar & to-dos", "Suggested prompts"].map((t) => (
              <div key={t} className="flex items-center gap-2 text-sm text-[#c7ccd6] mb-2.5">
                <Check size={16} className="text-[#9ca3af]" /> {t}
              </div>
            ))}
            <button data-testid="pricing-free" onClick={go}
              className="w-full mt-5 rounded-lg px-6 py-3 font-bold border border-[#2a3040] hover:border-[#FF5A00] transition-colors">
              Get Started
            </button>
          </div>
          <div className="relative bg-[#FF5A00]/10 border border-[#FF5A00]/40 rounded-2xl p-7">
            <span className="absolute -top-3 left-7 text-[10px] uppercase tracking-widest bg-[#FF5A00] text-[#0a0a0f] px-2.5 py-1 rounded-full font-bold">Most popular</span>
            <h3 className="font-display font-bold text-xl mb-1 flex items-center gap-2"><Crown size={18} className="text-[#FF5A00]" /> Pro</h3>
            <div className="font-display font-black text-4xl mb-1">$9.99<span className="text-base text-[#9ca3af] font-sans font-normal"> / mo</span></div>
            <div className="text-xs text-[#9ca3af] mb-5">or $99.99 / year — save 16%</div>
            {["Unlimited AI chat responses", "Longer, more detailed responses", "Unlimited saved conversation history", "Resume and cover letter builder", "Image generation responses", "Priority new features"].map((t) => (
              <div key={t} className="flex items-center gap-2 text-sm text-white mb-2.5">
                <Check size={16} className="text-[#FF5A00]" /> {t}
              </div>
            ))}
            <button data-testid="pricing-pro" onClick={go}
              className="w-full mt-5 rounded-lg px-6 py-3 font-bold bg-[#FF5A00] text-[#0a0a0f] hover:bg-[#FF7A33] transition-colors">
              Go Pro
            </button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-5 py-16">
        <div className="relative rounded-3xl border border-[#2a3040] bg-[#15151d] p-10 sm:p-14 text-center overflow-hidden">
          <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full bg-[#FF5A00]/15 blur-[120px] pointer-events-none" />
          <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tight mb-4 relative">
            Your <span className="text-[#FF5A00]">unfair advantage</span> awaits.
          </h2>
          <p className="text-[#9ca3af] mb-8 max-w-xl mx-auto relative">Join <Brand /> today — knowledge, news, and daily tools built for you.</p>
          <button data-testid="cta-get-started" onClick={go}
            className="relative inline-flex items-center gap-2 bg-[#FF5A00] text-[#0a0a0f] font-bold rounded-full px-8 py-4 hover:bg-[#FF7A33] transition-colors">
            Create your free account <ArrowRight size={18} />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#2a3040]">
        <div className="max-w-6xl mx-auto px-5 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Logo size="text-xl" />
          <div className="flex items-center gap-6 text-sm text-[#9ca3af]">
            <button onClick={() => navigate("/about")} className="hover:text-white transition-colors">About</button>
            <button onClick={() => navigate("/articles")} className="hover:text-white transition-colors">Articles</button>
            <button onClick={() => navigate("/privacy")} className="hover:text-white transition-colors">Privacy</button>
            <button onClick={go} className="hover:text-white transition-colors">Sign in</button>
          </div>
          <p className="text-xs text-[#6b7280]">© 2026 <Brand />. The Unfair Advantage.</p>
        </div>
      </footer>
    </div>
  );
}
